import * as React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { apiGet, apiPut, apiDelete } from "../api/client";

// ---- Tab Panel helper ----
const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

// ---- Unvalidated Practitioners Sub-component ----
const UnvalidatedPractitioners = () => {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [snackMessage, setSnackMessage] = React.useState(null);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [rejectConfirm, setRejectConfirm] = React.useState(null);

  const fetchUnvalidated = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await apiGet("/pro/unvalidated");
      const list = Array.isArray(json) ? json : json?.data ?? json?.content ?? [];
      setData(list);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchUnvalidated();
  }, [fetchUnvalidated]);

  const handleValidate = async (proId) => {
    try {
      setActionLoading(true);
      await apiPut(`/pro/${proId}/validate`);
      setSnackMessage("Professionnel valide avec succes.");
      fetchUnvalidated();
    } catch (e) {
      setSnackMessage(`Erreur: ${e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (proId) => {
    try {
      setActionLoading(true);
      await apiDelete(`/pro/${proId}`);
      setSnackMessage("Professionnel rejete avec succes.");
      setRejectConfirm(null);
      fetchUnvalidated();
    } catch (e) {
      setSnackMessage(`Erreur: ${e.message}`);
      setRejectConfirm(null);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      {snackMessage && (
        <Alert
          severity={snackMessage.startsWith("Erreur") ? "error" : "success"}
          sx={{ mb: 2 }}
          onClose={() => setSnackMessage(null)}
        >
          {snackMessage}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : data.length === 0 ? (
        <Alert severity="success" sx={{ mt: 2 }}>
          Aucun professionnel en attente de validation.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#ed6c02" }}>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>Nom</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>Téléphone</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>Specialite</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }}>Date d'inscription</TableCell>
                <TableCell sx={{ color: "white", fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                    {typeof row.id === "string" ? `${row.id.slice(0, 8)}...` : row.id}
                  </TableCell>
                  <TableCell>
                    {`${row.firstName || ""} ${row.lastName || ""}`.trim() || row.name || "---"}
                  </TableCell>
                  <TableCell>{row.email || "---"}</TableCell>
                  <TableCell>{row.phoneNumber || row.phone || row.address?.phone || "---"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.speciality || row.specialty || row.job || "---"}
                    />
                  </TableCell>
                  <TableCell>
                    {row.createdAt
                      ? new Date(row.createdAt).toLocaleDateString("fr-FR")
                      : row.registrationDate
                      ? new Date(row.registrationDate).toLocaleDateString("fr-FR")
                      : "---"}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Valider">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleValidate(row.id)}
                        disabled={actionLoading}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Rejeter">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setRejectConfirm(row)}
                        disabled={actionLoading}
                      >
                        <CancelIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Reject Confirmation Dialog */}
      <Dialog open={!!rejectConfirm} onClose={() => setRejectConfirm(null)}>
        <DialogTitle>Confirmer le rejet</DialogTitle>
        <DialogContent>
          <Typography>
            Voulez-vous vraiment rejeter le professionnel{" "}
            <strong>
              {rejectConfirm?.firstName} {rejectConfirm?.lastName}
            </strong>{" "}
            ({rejectConfirm?.email || rejectConfirm?.id}) ?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            Cette action supprimera le professionnel.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectConfirm(null)}>Annuler</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleReject(rejectConfirm?.id)}
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : "Rejeter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---- Main Practitioners List (existing functionality) ----
const PractitionersListTab = () => {
  const [specialities, setSpecialities] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedProId, setSelectedProId] = React.useState("");
  const [matching, setMatching] = React.useState(null);
  const [slots, setSlots] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const json = await apiGet("/users/specialities");
        const list = Array.isArray(json) ? json : json?.data ?? json?.specialities ?? [];
        if (!cancelled) setSpecialities(list);
      } catch (e) {
        if (!cancelled) setSpecialities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadMatching = async () => {
    if (!selectedProId.trim()) return;
    setMatching(null);
    try {
      const json = await apiGet(`/practitioners/${selectedProId.trim()}/matching`);
      setMatching(json);
    } catch (e) {
      if (e.status === 404) {
        setMatching({ notFound: true });
      } else {
        setMatching({ error: e.message });
      }
    }
  };

  const loadSlots = async () => {
    if (!selectedProId.trim()) return;
    setSlots(null);
    try {
      const json = await apiGet(`/calendars/users/${selectedProId.trim()}/available-slots`);
      setSlots(Array.isArray(json) ? json : json?.data ?? json?.slots ?? []);
    } catch (e) {
      if (e.status === 404) {
        setSlots({ notFound: true });
      } else {
        setSlots({ error: e.message });
      }
    }
  };

  const practitioners = specialities?.reduce((acc, s) => {
    const pros = s.practitioners ?? s.pros ?? s.users ?? [];
    pros.forEach((p) => acc.push({ ...p, specialityName: s.name ?? s.title ?? s.id }));
    return acc;
  }, []) ?? [];

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Donnees depuis GET /users/specialities. Matching : GET /practitioners/{"{professionalId}"}/matching. Creneaux : GET /calendars/users/{"{praticienId}"}/available-slots.
      </Alert>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>ID</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Nom / Email</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>Specialite</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {practitioners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                      Aucun praticien (ou structure differente de l'API)
                    </TableCell>
                  </TableRow>
                ) : (
                  practitioners.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.id}</TableCell>
                      <TableCell>{p.firstName || p.name || p.email || "---"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.specialityName || "---"} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Detail praticien (ID)
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <TextField
                    size="small"
                    label="ID professionnel"
                    value={selectedProId}
                    onChange={(e) => setSelectedProId(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={loadMatching}>
                    Matching ioUman
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="outlined" onClick={loadSlots}>
                    Creneaux dispo
                  </Button>
                </Grid>
              </Grid>
              {matching && (
                <Box sx={{ mt: 2 }}>
                  {matching.notFound && <Typography color="textSecondary">Matching non implemente ou 404.</Typography>}
                  {matching.error && <Typography color="error">{matching.error}</Typography>}
                  {!matching.notFound && !matching.error && (
                    <Typography>
                      Compatibilité : {typeof matching.matchingPercent === "number" ? `${matching.matchingPercent}%` : "non applicable (score calculé pour un patient et sa conversation guidée)"}
                      {(matching.experienceCount ?? 0) > 0 ? ` --- Expériences : ${matching.experienceCount}` : ""}
                    </Typography>
                  )}
                </Box>
              )}
              {slots != null && !Array.isArray(slots) && (
                <Box sx={{ mt: 2 }}>
                  {slots.error && <Typography color="error">{slots.error}</Typography>}
                  {slots.notFound && <Typography color="textSecondary">Creneaux non trouves.</Typography>}
                </Box>
              )}
              {Array.isArray(slots) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">Creneaux disponibles : {slots.length}</Typography>
                  <pre style={{ fontSize: 11, maxHeight: 200, overflow: "auto" }}>
                    {JSON.stringify(slots.slice(0, 5), null, 2)}
                  </pre>
                </Box>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

// ---- Main Export with Tabs ----
export const PractitionersList = () => {
  const [tabIndex, setTabIndex] = React.useState(0);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <MedicalServicesIcon /> Praticiens
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label="En attente de validation"
            icon={<HourglassEmptyIcon />}
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
          <Tab
            label="Tous les praticiens"
            icon={<MedicalServicesIcon />}
            iconPosition="start"
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Paper>

      <TabPanel value={tabIndex} index={0}>
        <UnvalidatedPractitioners />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <PractitionersListTab />
      </TabPanel>
    </Box>
  );
};
